import {
  Bath,
  BedDouble,
  BriefcaseBusiness,
  Check,
  DoorOpen,
  Hammer,
  Home,
  LampFloor,
  Layers3,
  Ruler,
  Sparkles,
  Trees
} from "lucide-react";
import type { ReactElement } from "react";
import {
  bathroomPackages,
  facadeMaterials,
  floorMaterials,
  formatPrice,
  houseModels,
  kitchenPackages,
  layouts,
  productOptions,
  roofMaterials,
  sizes,
  wallPackages,
  windowMaterials
} from "../data";
import { useConfiguratorStore } from "../store";
import type { StepId } from "../types";
import { MaterialGrid, SectionHeading, Stepper, ToggleRow } from "./Controls";

function ModelStep() {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const update = useConfiguratorStore((state) => state.update);

  return (
    <>
      <div className="panel-intro">
        <span>01</span>
        <div>
          <h2>Vælg dit hus</h2>
          <p>Start med den arkitektur, der passer til din hverdag.</p>
        </div>
      </div>
      <div className="model-list">
        {houseModels.map((model) => {
          const selected = configuration.modelId === model.id;
          return (
            <button
              className={`model-card ${selected ? "selected" : ""}`}
              key={model.id}
              type="button"
              onClick={() => update({ modelId: model.id })}
              aria-pressed={selected}
            >
              <span className={`house-mini shape-${model.shape}`} aria-hidden="true">
                <span className="mini-roof" />
                <span className="mini-body">
                  <i />
                  <i />
                  <i />
                </span>
              </span>
              <span className="model-card-copy">
                <span>
                  <strong>{model.name}</strong>
                  {model.demo && <small className="demo-label">Demo</small>}
                </span>
                <small>{model.tagline}</small>
                <b>Fra {formatPrice(model.basePrice)}</b>
              </span>
              <span className="selection-dot">{selected && <Check size={13} />}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

function LayoutStep() {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const update = useConfiguratorStore((state) => state.update);
  const setLayout = useConfiguratorStore((state) => state.setLayout);
  const currentSizeIndex = sizes.indexOf(configuration.areaM2);

  return (
    <>
      <div className="panel-intro">
        <span>02</span>
        <div>
          <h2>Størrelse & plan</h2>
          <p>Tilpas arealet og vælg den plan, der passer jer.</p>
        </div>
      </div>

      <SectionHeading aside={<strong className="accent-value">{configuration.areaM2} m²</strong>}>
        Husets størrelse
      </SectionHeading>
      <div className="size-control">
        <input
          aria-label="Husets størrelse"
          type="range"
          min={0}
          max={sizes.length - 1}
          step={1}
          value={currentSizeIndex}
          onChange={(event) => update({ areaM2: sizes[Number(event.target.value)] })}
        />
        <div className="range-labels">
          <span>{sizes[0]} m²</span>
          <span>{sizes.at(-1)} m²</span>
        </div>
        <div className="size-pills" aria-label="Vælg størrelse">
          {sizes.map((size) => (
            <button
              type="button"
              key={size}
              className={configuration.areaM2 === size ? "selected" : ""}
              onClick={() => update({ areaM2: size })}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      <SectionHeading>Plantegning</SectionHeading>
      <div className="layout-list">
        {layouts.map((layout) => {
          const unavailable = configuration.areaM2 < layout.minArea;
          const selected = configuration.layoutId === layout.id;
          return (
            <button
              key={layout.id}
              type="button"
              disabled={unavailable}
              className={`layout-card ${selected ? "selected" : ""}`}
              onClick={() => setLayout(layout.id)}
            >
              <span className="floorplan-mini" aria-hidden="true">
                <i /><i /><i /><i /><i />
              </span>
              <span>
                <strong>{layout.name}</strong>
                <small>{layout.bedrooms} værelser · {layout.bathrooms} bad</small>
                <small>{unavailable ? `Kræver ${layout.minArea} m²` : layout.description}</small>
              </span>
              {selected && <Check size={16} />}
            </button>
          );
        })}
      </div>

      <SectionHeading>Rum</SectionHeading>
      <div className="room-controls">
        <Stepper
          label="Værelser"
          value={configuration.bedrooms}
          min={2}
          max={6}
          onChange={(bedrooms) => update({ bedrooms })}
        />
        <Stepper
          label="Badeværelser"
          value={configuration.bathrooms}
          min={1}
          max={4}
          onChange={(bathrooms) => update({ bathrooms })}
        />
        <ToggleRow label="Hjemmekontor" checked={configuration.office} onChange={(office) => update({ office })} />
        <ToggleRow label="Bryggers" checked={configuration.utilityRoom} onChange={(utilityRoom) => update({ utilityRoom })} />
        <ToggleRow label="Separat entré" checked={configuration.separateEntrance} onChange={(separateEntrance) => update({ separateEntrance })} />
      </div>
    </>
  );
}

function ExteriorStep() {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const update = useConfiguratorStore((state) => state.update);

  return (
    <>
      <div className="panel-intro">
        <span>03</span>
        <div>
          <h2>Udvendigt design</h2>
          <p>Sammensæt husets materialer og udtryk.</p>
        </div>
      </div>
      <SectionHeading>Facade</SectionHeading>
      <MaterialGrid
        items={facadeMaterials}
        selected={configuration.facadeMaterialId}
        onSelect={(facadeMaterialId) => update({ facadeMaterialId })}
      />
      <SectionHeading>Tag</SectionHeading>
      <MaterialGrid
        items={roofMaterials}
        selected={configuration.roofMaterialId}
        onSelect={(roofMaterialId) => update({ roofMaterialId })}
      />
      <SectionHeading>Vinduesrammer</SectionHeading>
      <MaterialGrid
        items={windowMaterials}
        selected={configuration.windowColorId}
        onSelect={(windowColorId) => update({ windowColorId })}
      />
      <SectionHeading>Uderum</SectionHeading>
      <div className="toggle-group">
        <ToggleRow label="Terrasse" description="+ 74.000 kr." checked={configuration.terrace} onChange={(terrace) => update({ terrace })} />
        <ToggleRow
          label="Carport"
          description={configuration.areaM2 < 112 ? "Tilgængelig fra 112 m²" : "+ 128.000 kr."}
          checked={configuration.carport}
          disabled={configuration.areaM2 < 112}
          onChange={(carport) => update({ carport })}
        />
      </div>
    </>
  );
}

function InteriorStep() {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const update = useConfiguratorStore((state) => state.update);

  return (
    <>
      <div className="panel-intro">
        <span>04</span>
        <div>
          <h2>Indvendigt design</h2>
          <p>Skab ro og sammenhæng i husets indre.</p>
        </div>
      </div>
      <SectionHeading>Gulv</SectionHeading>
      <MaterialGrid
        items={floorMaterials}
        selected={configuration.floorMaterialId}
        onSelect={(floorMaterialId) => update({ floorMaterialId })}
      />
      <SectionHeading>Vægpalette</SectionHeading>
      <MaterialGrid
        items={wallPackages}
        selected={configuration.wallPackageId}
        onSelect={(wallPackageId) => update({ wallPackageId })}
      />
      <SectionHeading>Køkken</SectionHeading>
      <MaterialGrid
        items={kitchenPackages}
        selected={configuration.kitchenPackageId}
        onSelect={(kitchenPackageId) => update({ kitchenPackageId })}
      />
      <SectionHeading>Badeværelse</SectionHeading>
      <MaterialGrid
        items={bathroomPackages}
        selected={configuration.bathroomPackageId}
        onSelect={(bathroomPackageId) => update({ bathroomPackageId })}
      />
    </>
  );
}

const optionIcons = {
  energy: Sparkles,
  comfort: LampFloor,
  outdoor: Trees
};

function OptionsStep() {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const toggleOption = useConfiguratorStore((state) => state.toggleOption);

  return (
    <>
      <div className="panel-intro">
        <span>05</span>
        <div>
          <h2>Tilvalg</h2>
          <p>Vælg de løsninger, der gør huset til jeres.</p>
        </div>
      </div>
      <div className="option-list">
        {productOptions.map((option) => {
          const selected = configuration.options.includes(option.id);
          const Icon = optionIcons[option.category];
          return (
            <button
              key={option.id}
              className={`option-card ${selected ? "selected" : ""}`}
              type="button"
              onClick={() => toggleOption(option.id)}
              aria-pressed={selected}
            >
              <span className="option-icon"><Icon size={20} /></span>
              <span>
                <strong>{option.name}</strong>
                <small>{option.description}</small>
                <b>+ {formatPrice(option.price)}</b>
              </span>
              <span className="selection-dot">{selected && <Check size={13} />}</span>
            </button>
          );
        })}
      </div>
    </>
  );
}

const summaryIcons = [Home, Ruler, Layers3, BedDouble, Bath, BriefcaseBusiness, DoorOpen, Hammer];

function SummaryStep({ onQuote }: { onQuote: () => void }) {
  const configuration = useConfiguratorStore((state) => state.configuration);
  const model = houseModels.find((item) => item.id === configuration.modelId);
  const layout = layouts.find((item) => item.id === configuration.layoutId);
  const rows = [
    ["Husmodel", model?.name],
    ["Areal", `${configuration.areaM2} m²`],
    ["Plantegning", layout?.name],
    ["Værelser", String(configuration.bedrooms)],
    ["Badeværelser", String(configuration.bathrooms)],
    ["Kontor", configuration.office ? "Ja" : "Nej"],
    ["Facade", facadeMaterials.find((item) => item.id === configuration.facadeMaterialId)?.name],
    ["Tag", roofMaterials.find((item) => item.id === configuration.roofMaterialId)?.name]
  ];

  return (
    <>
      <div className="panel-intro">
        <span>06</span>
        <div>
          <h2>Dit hus er klar</h2>
          <p>Gennemgå valgene og send dem til vores byggerådgivere.</p>
        </div>
      </div>
      <div className="summary-detail-list">
        {rows.map(([label, value], index) => {
          const Icon = summaryIcons[index];
          return (
            <div key={label}>
              <Icon size={17} />
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          );
        })}
      </div>
      <div className="summary-options">
        <SectionHeading>Valgte tilvalg</SectionHeading>
        {configuration.options.length === 0 ? (
          <p>Ingen tilvalg valgt.</p>
        ) : (
          configuration.options.map((id) => (
            <span key={id}>
              <Check size={14} />
              {productOptions.find((item) => item.id === id)?.name}
            </span>
          ))
        )}
      </div>
      <button className="button button-primary full-width" type="button" onClick={onQuote}>
        Send tilbudsforespørgsel
      </button>
    </>
  );
}

const stepComponents: Record<Exclude<StepId, "summary">, () => ReactElement> = {
  model: ModelStep,
  layout: LayoutStep,
  exterior: ExteriorStep,
  interior: InteriorStep,
  options: OptionsStep
};

export function ConfigurationPanel({ onQuote }: { onQuote: () => void }) {
  const step = useConfiguratorStore((state) => state.step);
  const Component = step === "summary" ? null : stepComponents[step];

  return (
    <aside className="configuration-panel" aria-label="Konfigurationsvalg">
      <div className="configuration-panel-inner">
        {Component ? <Component /> : <SummaryStep onQuote={onQuote} />}
      </div>
    </aside>
  );
}
